
import { useEffect, useState } from "react";
import { assert } from "keycloakify/tools/assert";
import type { TemplateProps } from "keycloakify/login/TemplateProps";
import { getKcClsx } from "keycloakify/login/lib/kcClsx";
import { useSetClassName } from "keycloakify/tools/useSetClassName";
import { useInitialize } from "keycloakify/login/Template.useInitialize";
import type { I18n } from "keycloakify/login/i18n";
import type { KcContext } from "./KcContext";
import BioNeuralBackground from "./components/BioNeuralBackground";
import Link from "@mui/material/Link"; // Ensure Link is available if needed, or rely on usage later
import federatexMark from "../../assets/federatex-mark.svg";
import { Box, Typography } from "@mui/material";

export default function Template(props: TemplateProps<KcContext, I18n>) {
    const {
        displayInfo = false,
        displayMessage = true,
        displayRequiredFields = false,
        headerNode,
        socialProvidersNode = null,
        infoNode = null,
        documentTitle,
        bodyClassName,
        kcContext,
        i18n,
        doUseDefaultCss,
        children
    } = props;

    const { msg, msgStr, currentLanguage } = i18n;
    const { realm, locale, auth, url, message, isAppInitiatedAction } = kcContext;

    const { kcClsx } = getKcClsx({ doUseDefaultCss, classes: props.classes });

    useEffect(() => {
        document.title = documentTitle ?? "FederateX, Federated Learning Platform";
    }, [documentTitle, msg]);

    useSetClassName({
        qualifiedName: "html",
        className: kcClsx("kcHtmlClass")
    });

    useSetClassName({
        qualifiedName: "body",
        className: bodyClassName ?? kcClsx("kcBodyClass")
    });

    // Per-page card title so every Keycloak page reads cleanly (not a generic
    // "Welcome Back" on an error/reset page). Falls back to a string headerNode
    // when a page supplies one, else a sensible default.
    const PAGE_TITLES: Record<string, string> = {
        "login.ftl": "Welcome Back",
        "login-username.ftl": "Welcome Back",
        "login-password.ftl": "Enter your password",
        "register.ftl": "Create your account",
        "login-email.ftl": "Check your email",
        "login-otp.ftl": "Two-factor authentication",
        "login-reset-password.ftl": "Reset your password",
        "login-update-password.ftl": "Set a new password",
        "login-config-totp.ftl": "Set up your authenticator app",
        "login-verify-email.ftl": "Verify your email",
        "login-page-expired.ftl": "This link has expired",
        "logout-confirm.ftl": "Sign out",
        "info.ftl": "FederateX",
        "error.ftl": "Something went wrong",
        "terms.ftl": "Terms and Conditions",
    };
    const cardTitle =
        PAGE_TITLES[kcContext.pageId] ??
        (typeof headerNode === "string" ? headerNode : "Welcome Back");

    // Layout decision based on pageId
    const isLoginPage = kcContext.pageId === "login.ftl" || kcContext.pageId === "register.ftl" || kcContext.pageId === "login-reset-password.ftl";

    const { isReadyToRender } = useInitialize({ kcContext, doUseDefaultCss });

    if (!isReadyToRender) {
        return null;
    }




    // Default Layout (Centered Card) for other pages (like Error, Info)
    return (
        <Box
            sx={{
                position: "relative",
                width: "100vw",
                height: "100vh",
                overflow: "hidden",
                background: "linear-gradient(160deg, #0b1629 0%, #132643 55%, #0b1629 100%)",
                color: "#e2e8f0",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
             <Box
                sx={{
                    position: "relative",
                    mb: 4,
                    zIndex: 20,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25
                }}
                aria-label="FederateX"
            >
                <Box
                    component="img"
                    src={federatexMark}
                    alt="FederateX"
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "6px",
                        filter: "drop-shadow(0 0 20px rgba(125, 211, 252, 0.3))"
                    }}
                />
                <Typography
                    sx={{
                        color: "#ffffff",
                        fontWeight: 800,
                        fontSize: "1.25rem",
                        letterSpacing: "0.04em",
                        lineHeight: 1,
                        whiteSpace: "nowrap"
                    }}
                >
                    FEDERATE<Box component="span" sx={{ color: "#36CE7B" }}>X</Box>
                </Typography>
            </Box>

            {/* Background Layer */}
            <Box sx={{ position: "absolute", inset: 0, zIndex: 0 }}>
                <BioNeuralBackground transparent />
            </Box>

            {/* Dot Matrix Overlay - Matched to AuthLoading */}
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 1,
                    backgroundImage: "radial-gradient(rgba(255, 255, 255, 0.15) 1px, transparent 1px)",
                    backgroundSize: "30px 30px",
                    maskImage: "radial-gradient(circle at center, black 40%, transparent 100%)",
                    WebkitMaskImage: "radial-gradient(circle at center, black 40%, transparent 100%)",
                    pointerEvents: "none",
                }}
            />

            {/* Glassmorphism Card */}
            <Box
                sx={{
                    position: "relative",
                    zIndex: 10,
                    width: "100%",
                    maxWidth: "500px",
                    p: 5, // Increased padding
                    mx: 2,
                    borderRadius: 4,
                    backdropFilter: "blur(12px)",
                    backgroundColor: "rgba(255, 255, 255, 0.03)", 
                    borderTop: "1px solid rgba(255, 255, 255, 0.2)",
                    borderLeft: "1px solid rgba(255, 255, 255, 0.05)",
                    borderRight: "1px solid rgba(255, 255, 255, 0.05)",
                    borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    '&:hover': {
                         boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
                    }
                }}
            >
                {/* Header */}
                <Box mb={5} textAlign="center">
                    <Typography
                        variant="h4"
                        component="h1"
                        sx={{
                            fontFamily: "Inter, sans-serif",
                            fontWeight: 700,
                            color: "#fff",
                            letterSpacing: "-0.02em",
                            textShadow: "0 0 20px rgba(125, 211, 252, 0.3)"
                        }}
                    >
                        {cardTitle}
                    </Typography>
                </Box>

                {/* Server message banner. Custom pages pass displayMessage={false}
                    and render their own; DefaultPage-driven pages (info, error,
                    page-expired, verify-email) rely on this so they're never blank. */}
                {displayMessage && message !== undefined && message.summary &&
                  (message.type !== "warning" || !isAppInitiatedAction) && (
                     <Box
                        sx={{
                            width: "100%",
                            mb: 3,
                            p: 2,
                            borderRadius: 2,
                            backgroundColor:
                                message.type === "error" ? "rgba(239, 68, 68, 0.1)" :
                                message.type === "success" ? "rgba(54, 206, 123, 0.1)" :
                                message.type === "warning" ? "rgba(251, 191, 36, 0.1)" :
                                "rgba(125, 211, 252, 0.1)",
                            border: `1px solid ${
                                message.type === "error" ? "rgba(239, 68, 68, 0.25)" :
                                message.type === "success" ? "rgba(54, 206, 123, 0.3)" :
                                message.type === "warning" ? "rgba(251, 191, 36, 0.3)" :
                                "rgba(125, 211, 252, 0.25)"}`,
                            color:
                                message.type === "error" ? "#fca5a5" :
                                message.type === "success" ? "#6ee7b7" :
                                message.type === "warning" ? "#fcd34d" :
                                "#7dd3fc",
                            fontSize: "0.875rem",
                            lineHeight: 1.5,
                        }}
                        dangerouslySetInnerHTML={{ __html: message.summary }}
                     />
                )}

                {/* Main Content */}
                <Box width="100%">
                    {children}
                </Box>

                {/* Footer / Language */}
                {realm.internationalizationEnabled && (assert(locale), true) && locale.supported.length > 1 && (
                    <Box mt={3} display="flex" justifyContent="center">
                         <div className={kcClsx("kcLocaleWrapperClass")}>
                            <div className={kcClsx("kcLocaleClass")}>
                                <ul className={kcClsx("kcLocaleListClass")} style={{ listStyle: 'none', padding: 0, display: 'flex', gap: '1rem' }}>
                                    {locale.supported.map(({ languageTag, url, label }) => (
                                        <li key={languageTag} className={kcClsx("kcLocaleListItemClass")}>
                                            <a 
                                                href={url} 
                                                className={kcClsx("kcLocaleItemClass")}
                                                 style={{ 
                                                    color: languageTag === currentLanguage.languageTag ? "#7dd3fc" : "rgba(255,255,255,0.6)",
                                                    textDecoration: 'none',
                                                    fontSize: '0.875rem'
                                                }}
                                            >
                                                {label}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </Box>
                )}

                {/* Centered Footer Link */}
                <Box mt={2}>
                    <Link
                        href="#"
                        sx={{
                            color: "#ffffff",
                            fontSize: "14px",
                            textDecoration: "none",
                            opacity: 0.7,
                            "&:hover": {
                                opacity: 1,
                                textDecoration: "underline"
                            }
                        }}
                    >
                        Terms and Conditions
                    </Link>
                </Box>
            </Box>
        </Box>
    );
}

